/**
 * Unified D3 Legend System
 *
 * Pure D3.js native implementation for consistent rower legends across all charts.
 * Uses only standard D3 data binding, SVG elements, and native interactions.
 *
 * Design Standards:
 * - 12px circles with white fill and colored borders (ring style)
 * - Inter font, 12px text labels
 * - Consistent spacing and alignment
 * - Native D3 interactions and accessibility
 */

import * as d3 from 'd3';
import { getRowerColorByKey } from './rowerColors';
import { getCssToken } from '../theme/getCssToken';
import { LIGHT_HEX, TOKEN_CSS_VARS } from '../theme/tokens';

export interface LegendItem {
  key: string; // Rower key (e.g., 'rower1', 'Stroke')
  position: number; // Rower position number
  label?: string; // Custom label (defaults to R{position})
  color?: string; // Custom color (defaults to getRowerColorByKey)
}

export interface LegendConfig {
  position: 'top' | 'bottom' | 'left' | 'right';
  orientation: 'horizontal' | 'vertical';
  itemSpacing: number;
  showLabels: boolean;
  interactive: boolean;
  compact: boolean; // Smaller size for inline usage
  onItemClick?: (item: LegendItem) => void; // Optional click handler provided by chart
  swatchRadius: number;
  swatchStrokeWidth: number;
  offsetX: number;
  offsetY: number;
}

export const defaultLegendConfig: LegendConfig = {
  position: 'bottom',
  orientation: 'horizontal',
  itemSpacing: 80,
  showLabels: true,
  interactive: true,
  compact: false,
  swatchRadius: 6,
  swatchStrokeWidth: 2,
  offsetX: 0,
  offsetY: 0,
};

/**
 * Create unified D3 legend using pure D3 native elements
 *
 * @param parent - D3 selection where legend should be appended
 * @param items - Array of legend items to display
 * @param config - Legend configuration options
 * @returns D3 selection of the created legend group
 */
export function createUnifiedLegend(
  parent: d3.Selection<any, any, any, any>,
  items: LegendItem[],
  config: Partial<LegendConfig> = {}
): d3.Selection<SVGGElement, any, any, any> {
  const finalConfig: LegendConfig = { ...defaultLegendConfig, ...config };
  const {
    orientation,
    itemSpacing,
    showLabels,
    interactive,
    compact,
    onItemClick,
    swatchRadius,
    swatchStrokeWidth,
  } = finalConfig;

  // Circle and text sizing
  const circleRadius = compact ? Math.min(swatchRadius, 5) : swatchRadius;
  const strokeWidth = compact ? Math.min(swatchStrokeWidth, 1.5) : swatchStrokeWidth;
  const fontSize = compact ? '10px' : '12px';
  const textOffset = circleRadius + (compact ? 6 : 10);

  // Create legend group
  const legend = parent
    .append('g')
    .attr('class', 'unified-legend')
    .attr('role', 'img')
    .attr('aria-label', 'Chart legend');

  // Create legend items using native D3 data binding
  const legendItems = legend
    .selectAll('.legend-item')
    .data(items)
    .enter()
    .append('g')
    .attr('class', 'legend-item')
    .attr('role', 'img')
    .attr('aria-label', (d) => `${d.label || `Rower ${d.position}`}`)
    .attr('transform', (d, i) => {
      if (orientation === 'horizontal') {
        return `translate(${i * itemSpacing}, 0)`;
      } else {
        return `translate(0, ${i * itemSpacing})`;
      }
    });

  // Add circles using native D3 SVG elements
  legendItems
    .append('circle')
    .attr('cx', 0)
    .attr('cy', 0)
    .attr('r', circleRadius)
    .attr('fill', '#ffffff')
    .attr('stroke', (d) => d.color || getRowerColorByKey(d.key))
    .attr('stroke-width', strokeWidth)
    .style('filter', 'drop-shadow(0 1px 2px rgba(0,0,0,0.04))')
    .style('cursor', interactive ? 'pointer' : 'default');

  // Add text labels using native D3 text elements
  if (showLabels) {
    const labels = legendItems
      .append('text')
      .attr('x', textOffset)
      .attr('y', 0)
      .attr('dy', '0.35em')
      .attr('font-family', 'Inter, sans-serif')
      .attr('font-size', fontSize)
      .attr('font-weight', '500')
      .attr('class', 'legend-label')
      .attr('fill', getCssToken(TOKEN_CSS_VARS.gray[700], LIGHT_HEX.gray[700]))
      .style('cursor', interactive ? 'pointer' : 'default')
      .text((d) => d.label || `R${d.position}`);

    // Add accessibility for text
    labels.attr('aria-hidden', 'true'); // Text is redundant with group aria-label
  }

  // Add native D3 interactions if enabled
  if (interactive) {
    legendItems
      .style('cursor', 'pointer')
      .style('outline', 'none')
      .on('mouseover', function () {
        // Native D3 hover effect
        d3.select(this)
          .select('circle')
          .transition()
          .duration(150)
          .attr('stroke-width', strokeWidth + 1)
          .style('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))');
      })
      .on('mouseout', function () {
        // Restore original state
        d3.select(this)
          .select('circle')
          .transition()
          .duration(150)
          .attr('stroke-width', strokeWidth)
          .style('filter', 'drop-shadow(0 1px 2px rgba(0,0,0,0.04))');
      })
      .on('focus', function () {
        d3.select(this)
          .select('circle')
          .transition()
          .duration(150)
          .attr('stroke-width', strokeWidth + 1)
          .style('filter', 'drop-shadow(0 2px 6px rgba(30, 64, 175, 0.25))');
      })
      .on('blur', function () {
        d3.select(this)
          .select('circle')
          .transition()
          .duration(150)
          .attr('stroke-width', strokeWidth)
          .style('filter', 'drop-shadow(0 1px 2px rgba(0,0,0,0.04))');
      })
      .on('click', function (event, d) {
        // Dispatch custom event for legend click handling
        const customEvent = new CustomEvent('legendItemClick', {
          detail: { item: d, element: this },
        });
        event.target.dispatchEvent(customEvent);
        onItemClick?.(d);
      })
      .on('keydown', function (event, d) {
        // Keyboard accessibility
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          const customEvent = new CustomEvent('legendItemClick', {
            detail: { item: d, element: this },
          });
          event.target.dispatchEvent(customEvent);
          onItemClick?.(d);
        }
      });

    // Make focusable for keyboard navigation
    legendItems.attr('tabindex', '0');
  }

  return legend;
}

/**
 * Position legend within chart boundaries
 *
 * @param legend - D3 selection of legend group
 * @param chartDimensions - Chart width and height
 * @param config - Legend configuration
 */
export function positionLegend(
  legend: d3.Selection<SVGGElement, any, any, any>,
  chartDimensions: { width: number; height: number },
  config: Partial<LegendConfig> = {}
): void {
  const finalConfig = { ...defaultLegendConfig, ...config };
  const { position, offsetX, offsetY } = finalConfig;
  const { width, height } = chartDimensions;

  // Calculate legend dimensions
  const legendBBox = (legend.node() as SVGGElement).getBBox();
  const legendWidth = legendBBox.width;
  const legendHeight = legendBBox.height;

  let x = 0;
  let y = 0;

  switch (position) {
    case 'top':
      x = (width - legendWidth) / 2;
      y = 20;
      break;
    case 'bottom':
      x = (width - legendWidth) / 2;
      y = height - legendHeight - 20;
      break;
    case 'left':
      x = 20;
      y = (height - legendHeight) / 2;
      break;
    case 'right':
      x = width - legendWidth - 20;
      y = (height - legendHeight) / 2;
      break;
  }

  legend.attr('transform', `translate(${x + offsetX}, ${y + offsetY})`);
}

/**
 * Create inline legend for table rows or compact displays
 *
 * @param parent - D3 selection where inline legend should be appended
 * @param item - Single legend item
 * @returns D3 selection of the created inline legend
 */
export function createInlineLegend(
  parent: d3.Selection<any, any, any, any>,
  item: LegendItem
): d3.Selection<SVGGElement, any, any, any> {
  return createUnifiedLegend(parent, [item], {
    orientation: 'horizontal',
    showLabels: false,
    interactive: false,
    compact: true,
    itemSpacing: 0,
    swatchRadius: 5,
    swatchStrokeWidth: 1.5,
  });
}

/**
 * Update legend data dynamically using D3 data binding
 *
 * @param legend - Existing legend D3 selection
 * @param newItems - Updated array of legend items
 * @param config - Legend configuration
 */
export function updateLegendData(
  legend: d3.Selection<SVGGElement, any, any, any>,
  newItems: LegendItem[],
  config: Partial<LegendConfig> = {}
): void {
  const finalConfig = { ...defaultLegendConfig, ...config };
  const { orientation, itemSpacing, swatchRadius, swatchStrokeWidth } = finalConfig;

  // Native D3 data binding for updates
  const legendItems = legend.selectAll('.legend-item').data(newItems, (d: any) => d.key);

  // Handle removed items
  legendItems.exit().transition().duration(300).style('opacity', 0).remove();

  // Handle new items
  const enterItems = legendItems
    .enter()
    .append('g')
    .attr('class', 'legend-item')
    .style('opacity', 0);

  // Add circles and text to new items (same as createUnifiedLegend)
  enterItems
    .append('circle')
    .attr('cx', 0)
    .attr('cy', 0)
    .attr('r', swatchRadius)
    .attr('fill', '#ffffff')
    .attr('stroke', (d) => d.color || getRowerColorByKey(d.key))
    .attr('stroke-width', swatchStrokeWidth);

  if (finalConfig.showLabels) {
    enterItems
      .append('text')
      .attr('x', swatchRadius + 10)
      .attr('y', 0)
      .attr('dy', '0.35em')
      .attr('font-family', 'Inter, sans-serif')
      .attr('font-size', '12px')
      .text((d) => d.label || `R${d.position}`);
  }

  // Update positions for all items
  legendItems
    .merge(enterItems)
    .transition()
    .duration(300)
    .style('opacity', 1)
    .attr('transform', (d, i) => {
      if (orientation === 'horizontal') {
        return `translate(${i * itemSpacing}, 0)`;
      } else {
        return `translate(0, ${i * itemSpacing})`;
      }
    });
}

/**
 * Create legend data from rower configuration
 *
 * @param rowerKeys - Array of rower keys
 * @param rowerPositions - Optional array of rower positions (defaults to index + 1)
 * @returns Array of LegendItem objects
 */
export function createLegendData(rowerKeys: string[], rowerPositions?: number[]): LegendItem[] {
  return rowerKeys.map((key, index) => ({
    key,
    position: rowerPositions ? rowerPositions[index] : index + 1,
    color: getRowerColorByKey(key),
  }));
}
